import { Cell } from '@table-library/react-table-library/table';
import { CellTree } from '@table-library/react-table-library/tree';
import Sparkline from '../../../components/Sparklines';
import numeral from 'numeral';
import DeleteButton from './DeleteButton';
import { collectCell, getPercent } from '../../../helpers/showDelta';

export const COLUMNS_1 = (deleteItem, t) => [
    {
        label: t('search.keyword_clusters.columns.cluster_name'),
        key: 'cluster_name',
        data: 'cluster_name',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;

            return (
                <strong>
                    <a
                        href={`/domains/${
                            window.info.id
                        }/gsc-reports/keyword-clusters/cluster?cluster=${encodeURIComponent(data)}`}
                        className="d-inline-flex align-items-center justify-content-center"
                        rel="noreferrer"
                    >
                        {data}
                    </a>
                </strong>
            );
        },
    },
    {
        label: t('search.keyword_clusters.columns.keywords'),
        key: 'weekly_keywords',
        data: 'weekly_keywords',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong>{Math.round(data)}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.trend'),
        key: 'keywords_sparkline',
        data: 'keywords_sparkline',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '-';
            return (
                <Sparkline
                    dataL={item.keywordsL}
                    keyName="Keywords"
                    labels={item.weeksL.map((x) => x.value)}
                />
            );
        },
    },
    {
        label: t('search.keyword_clusters.columns.clicks'),
        key: 'clicks',
        data: 'clicks',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong>{numeral(data).format('0.0a')}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.trend'),
        key: 'clicks_sparkline',
        data: 'clicks_sparkline',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '-';
            return (
                <Sparkline
                    dataL={item.clicksL}
                    keyName="Clicks"
                    labels={item.weeksL.map((x) => x.value)}
                />
            );
        },
    },
    {
        label: <span className="delta-sign">&#916;</span>,
        key: 'clicks_delta',
        data: 'clicks_delta',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return getPercent(item.clicksL.at(-1), item.clicksL[0]);
            return collectCell(item.clicksL.at(-1), item.clicksL[0]);
        },
    },
    {
        label: t('search.keyword_clusters.columns.impressions'),
        key: 'impressions',
        data: 'impressions',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong>{numeral(data).format('0.0a')}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.urls'),
        key: 'weekly_urls',
        data: 'weekly_urls',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong>{numeral(data).format('0.0a')}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.trend'),
        key: 'urls_sparkline',
        data: 'urls_sparkline',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '-';
            return (
                <Sparkline
                    dataL={item.urlsL}
                    keyName="URLs"
                    labels={item.weeksL.map((x) => x.value)}
                />
            );
        },
    },
    {
        label: t('search.keyword_clusters.columns.position'),
        key: 'avgPosition',
        data: 'avgPosition',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong>{numeral(data).format('0.0a')}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.lifespan'),
        key: 'lifespan',
        data: 'lifespan',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong>{numeral(data).format('0.0a')}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.ctr'),
        key: 'avgctr',
        data: 'avgctr',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            const ctrValue = item.clicks / item.impressions;
            if (csvDownload) return ctrValue;
            return <strong>{numeral(ctrValue).format('0.00%')}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns.actions'),
        key: 'actions',
        data: 'actions',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return null;
            return <DeleteButton clusterName={item.cluster_name} deleteItem={deleteItem} />;
        },
    },
];

export const COLUMNS_2 = (deleteItem, t) => [
    {
        label: t('search.keyword_clusters.columns_2.cluster'),
        key: 'keyword',
        data: 'keyword',
        Component: CellTree,
        renderCell: ({ data, item }) => {
            if (item.isClusterName) {
                return (
                    <strong>
                        <a
                            href={`/domains/${window.info.id}/gsc-reports/keyword-clusters/cluster?cluster=${data}`}
                            className="d-inline-flex align-items-center justify-content-center"
                            rel="noreferrer"
                        >
                            {data}
                        </a>
                    </strong>
                );
            }
            return <strong>{data}</strong>;
        },
    },
    {
        label: t('search.keyword_clusters.columns_2.keywords'),
        key: 'keywords',
        data: 'keywords',
        Component: Cell,
        renderCell: ({ data }) => <strong>{numeral(data).format('0 a')}</strong>,
    },
    {
        label: t('search.keyword_clusters.columns_2.avg_weekly_urls'),
        key: 'urls',
        data: 'urls',
        Component: Cell,
        renderCell: ({ data }) => <strong>{numeral(data).format('0.0a')}</strong>,
    },
    {
        label: t('search.keyword_clusters.columns_2.clicks'),
        key: 'clicks',
        data: 'clicks',
        Component: Cell,
        renderCell: ({ data }) => <strong>{numeral(data).format('0.0a')}</strong>,
    },
    {
        label: t('search.keyword_clusters.columns_2.impressions'),
        key: 'impressions',
        data: 'impressions',
        Component: Cell,
        renderCell: ({ data }) => <strong>{numeral(data).format('0.0a')}</strong>,
    },
    {
        label: t('search.keyword_clusters.columns_2.avg_position'),
        key: 'position',
        data: 'position',
        Component: Cell,
        renderCell: ({ data }) => <strong>{numeral(data).format('0.0a')}</strong>,
    },
    {
        label: t('search.keyword_clusters.columns_2.lifespan'),
        key: 'lifespan',
        data: 'lifespan',
        Component: Cell,
        renderCell: ({ data }) => <strong>{numeral(data).format('0.0a')}</strong>,
    },
    {
        label: t('search.keyword_clusters.columns_2.ctr'),
        key: 'ctr',
        data: 'ctr',
        Component: Cell,
        renderCell: ({ item }) => (
            <div>
                <strong>{numeral(item.clicks / item.impressions).format('0.00%')}</strong>
            </div>
        ),
    },
    {
        label: t('search.keyword_clusters.columns_2.actions'),
        key: 'actions',
        data: 'actions',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload || !item.isClusterName) return null;
            return <DeleteButton clusterName={item.keyword} deleteItem={deleteItem} />;
        },
    },
];
